import { EventEmitter } from 'eventemitter3';
import { BroadcastStateService } from '../broadcaststate/BroadcastStateService';
import { StoredMediaService } from '../storedmedia/StoredMediaService';
import { StreamrAppService } from '../streamrapp/StreamrAppService';
import { createDefaultSerializableScene, createDefaultSerializableVideoSlot, } from './Scene';
import { SceneServiceOutput } from './SceneServiceOutput';
import { SceneStore } from './SceneStore';
export class SceneService extends EventEmitter {
    static MAX_TRANSFER_RETRIES = 3;
    static instance;
    output;
    sceneStore;
    displaySceneId;
    liveSceneId;
    lastEmittedDisplayScene;
    lastEmittedLiveScene;
    transferredForSceneId;
    transferRetries = {};
    static getInstance() {
        if (!SceneService.instance) {
            SceneService.instance = new SceneService();
        }
        return SceneService.instance;
    }
    static getNotificationInterface() {
        return SceneService.getInstance();
    }
    static getOutput() {
        return SceneServiceOutput.getReactiveOutput();
    }
    constructor() {
        super();
        this.output = new SceneServiceOutput();
        this.sceneStore = new SceneStore();
        this.publishToOutput();
        this.output.setThumbnails(this.sceneStore.getThumbnails());
        this.restoreSceneState();
    }
    getDisplayScene() {
        return this.resolveDisplayScene();
    }
    getLiveScene() {
        return this.resolveLiveScene();
    }
    resolveDisplayScene() {
        const scenes = this.sceneStore.getScenes();
        if (this.displaySceneId) {
            return scenes.find((s) => s.id === this.displaySceneId);
        }
        if (this.liveSceneId) {
            return scenes.find((s) => s.id === this.liveSceneId);
        }
        return scenes[0];
    }
    resolveLiveScene() {
        if (!this.liveSceneId)
            return undefined;
        return this.sceneStore
            .getScenes()
            .find((s) => s.id === this.liveSceneId);
    }
    emitIfChanged() {
        const displayScene = this.resolveDisplayScene();
        const liveScene = this.resolveLiveScene();
        if (displayScene !== this.lastEmittedDisplayScene) {
            this.lastEmittedDisplayScene = displayScene;
            this.emit('displaySceneChanged', displayScene);
        }
        if (liveScene !== this.lastEmittedLiveScene) {
            this.lastEmittedLiveScene = liveScene;
            this.emit('liveSceneChanged', liveScene);
        }
    }
    restoreSceneState() {
        const scenes = this.sceneStore.getScenes();
        if (scenes.length === 0)
            return;
        const persistedLiveId = this.sceneStore.getLiveSceneId();
        if (persistedLiveId && scenes.some((s) => s.id === persistedLiveId)) {
            this.liveSceneId = persistedLiveId;
            this.output.setLiveSceneId(persistedLiveId);
        }
        else {
            this.liveSceneId = scenes[0].id;
            this.sceneStore.setLiveSceneId(scenes[0].id);
            this.output.setLiveSceneId(scenes[0].id);
        }
        const persistedDisplayId = this.sceneStore.getDisplaySceneId();
        if (persistedDisplayId &&
            scenes.some((s) => s.id === persistedDisplayId)) {
            this.displaySceneId = persistedDisplayId;
            this.output.setDisplaySceneId(persistedDisplayId);
        }
        else {
            this.displaySceneId = this.liveSceneId;
            this.sceneStore.setDisplaySceneId(this.liveSceneId);
            this.output.setDisplaySceneId(this.liveSceneId);
        }
    }
    publishToOutput() {
        this.output.setScenes(this.sceneStore.getScenes());
    }
    notifyAddScene() {
        const scenes = this.sceneStore.getScenes();
        const name = `Scene ${scenes.length + 1}`;
        const scene = createDefaultSerializableScene(name);
        this.sceneStore.addScene(scene);
        this.publishToOutput();
        this.displaySceneId = scene.id;
        this.sceneStore.setDisplaySceneId(scene.id);
        this.output.setDisplaySceneId(scene.id);
        this.emitIfChanged();
    }
    notifyUpdateScene(scene) {
        this.sceneStore.updateScene(scene);
        this.publishToOutput();
        this.emitIfChanged();
    }
    notifyDeleteScene(id) {
        if (this.sceneStore.getScenes().length <= 1)
            return;
        this.sceneStore.deleteScene(id);
        this.sceneStore.removeThumbnail(id);
        this.publishToOutput();
        const remaining = this.sceneStore.getScenes();
        if (this.displaySceneId === id) {
            const newId = remaining.length > 0 ? remaining[0].id : undefined;
            this.displaySceneId = newId;
            this.sceneStore.setDisplaySceneId(newId);
            this.output.setDisplaySceneId(newId);
        }
        if (this.liveSceneId === id) {
            this.liveSceneId = undefined;
            this.sceneStore.setLiveSceneId(undefined);
            this.output.setLiveSceneId(undefined);
            this.handleLiveSceneTransition(id, undefined);
        }
        this.emitIfChanged();
    }
    notifySetLiveScene(id) {
        const prevId = this.liveSceneId;
        this.liveSceneId = id;
        this.displaySceneId = id;
        this.sceneStore.setLiveSceneId(id);
        this.sceneStore.setDisplaySceneId(id);
        this.output.setLiveSceneId(id);
        this.output.setDisplaySceneId(id);
        this.handleLiveSceneTransition(prevId, id);
        this.emitIfChanged();
    }
    notifySetDisplayScene(id) {
        this.displaySceneId = id;
        this.sceneStore.setDisplaySceneId(id);
        this.output.setDisplaySceneId(id);
        this.emitIfChanged();
    }
    notifyLiveSceneCanvasMounted(sceneId, canvas) {
        if (sceneId !== this.liveSceneId)
            return;
        if (this.transferredForSceneId === sceneId)
            return;
        try {
            const offscreen = canvas.transferControlToOffscreen();
            StreamrAppService.getNotificationInterface().notifyLiveSceneThumbnailCanvasMounted(offscreen);
            this.transferredForSceneId = sceneId;
            this.transferRetries[sceneId] = 0;
        }
        catch (err) {
            const retries = this.transferRetries[sceneId] ?? 0;
            if (retries < SceneService.MAX_TRANSFER_RETRIES) {
                this.transferRetries[sceneId] = retries + 1;
                this.output.rekeySceneCanvas(sceneId);
            }
            else {
                console.warn(`transferControlToOffscreen failed after ${SceneService.MAX_TRANSFER_RETRIES} retries for scene "${sceneId}"`, err);
            }
        }
    }
    notifyLiveSceneCanvasUnmounted(sceneId) {
        if (this.transferredForSceneId === sceneId) {
            this.transferredForSceneId = undefined;
        }
        this.transferRetries[sceneId] = 0;
    }
    handleLiveSceneTransition(prevId, newId) {
        if (prevId && prevId !== newId) {
            this.transferRetries[prevId] = 0;
            this.output.rekeySceneCanvas(prevId);
        }
        if (this.transferredForSceneId !== newId) {
            this.transferredForSceneId = undefined;
        }
        if (!newId) {
            StreamrAppService.getNotificationInterface().notifyLiveSceneThumbnailCanvasMounted();
        }
    }
    notifySetSceneLayoutId(sceneId, layoutId) {
        const scenes = this.sceneStore.getScenes();
        const scene = scenes.find((s) => s.id === sceneId);
        if (!scene)
            return;
        let updated = { ...scene, layoutId };
        if (layoutId === 'manual' &&
            updated.backgroundSourceId === undefined &&
            updated.backgroundColor === undefined) {
            // Seed the background slot with the user's own video so that
            // switching from an automatic layout does not produce an empty
            // stage. The background stays clearable afterwards.
            const ownUserId = BroadcastStateService.getInstance().getActiveBroadcast()
                ?.broadcasterUserId;
            if (ownUserId) {
                updated = { ...updated, backgroundSourceId: ownUserId };
            }
        }
        this.sceneStore.updateScene(updated);
        this.publishToOutput();
        this.emitIfChanged();
    }
    /**
     * Adds a video slot to the scene. `init` overrides any of the default
     * slot's fields — pass a caller-generated id when the caller needs to
     * reference the new slot afterwards.
     */
    notifyAddVideoSlot(sceneId, init) {
        const scenes = this.sceneStore.getScenes();
        const scene = scenes.find((s) => s.id === sceneId);
        if (!scene) {
            return;
        }
        const updatedScene = {
            ...scene,
            videoSlots: [
                ...scene.videoSlots,
                { ...createDefaultSerializableVideoSlot(), ...init },
            ],
        };
        this.sceneStore.updateScene(updatedScene);
        this.publishToOutput();
        this.emitIfChanged();
    }
    notifyRemoveVideoSlot(sceneId, slotId) {
        const scenes = this.sceneStore.getScenes();
        const scene = scenes.find((s) => s.id === sceneId);
        if (!scene) {
            return;
        }
        const updatedScene = {
            ...scene,
            videoSlots: scene.videoSlots.filter((s) => s.id !== slotId),
        };
        this.sceneStore.updateScene(updatedScene);
        this.publishToOutput();
        this.emitIfChanged();
    }
    notifySetSceneThumbnail(sceneId, dataUrl) {
        this.sceneStore.setThumbnail(sceneId, dataUrl);
        this.output.setThumbnail(sceneId, dataUrl);
    }
    async resolveScene(scene) {
        const storedMedia = StoredMediaService.getInstance();
        const layoutId = scene.layoutId ?? 'auto';
        let videoSlots;
        if (layoutId === 'manual' && scene.videoSlots.length > 0) {
            videoSlots = await Promise.all(scene.videoSlots.map(async (slot) => {
                const isImage = storedMedia.isImageSourceId(slot.sourceId);
                let imageBitmap;
                if (isImage) {
                    const src = storedMedia.getImageBitmap(slot.sourceId);
                    if (src) {
                        imageBitmap = await createImageBitmap(src);
                    }
                }
                return {
                    x: slot.x,
                    y: slot.y,
                    width: slot.width,
                    height: slot.height,
                    zIndex: slot.zIndex ?? 0,
                    sourceId: isImage ? undefined : slot.sourceId,
                    imageBitmap,
                    transparentBackground: slot.transparentBackground,
                };
            }));
        }
        let background;
        const bgSourceId = scene.backgroundSourceId;
        const bgColor = scene.backgroundColor;
        if (bgSourceId) {
            if (storedMedia.isImageSourceId(bgSourceId)) {
                const src = storedMedia.getImageBitmap(bgSourceId);
                if (src) {
                    background = {
                        type: 'image',
                        bitmap: await createImageBitmap(src),
                    };
                }
            }
            else {
                background = { type: 'track', trackId: bgSourceId };
            }
        }
        else if (bgColor) {
            background = { type: 'color', value: bgColor };
        }
        return {
            id: scene.id,
            layoutId,
            videoSlots,
            background,
        };
    }
    notifySetSceneBackground(sceneId, backgroundColor) {
        const scenes = this.sceneStore.getScenes();
        const scene = scenes.find((s) => s.id === sceneId);
        if (!scene)
            return;
        this.sceneStore.updateScene({
            ...scene,
            backgroundColor,
            backgroundSourceId: undefined,
        });
        this.publishToOutput();
        this.emitIfChanged();
    }
    notifySetBackgroundImage(sceneId, file) {
        void StoredMediaService.getInstance()
            .addImage(file)
            .then((image) => {
            const scenes = this.sceneStore.getScenes();
            const scene = scenes.find((s) => s.id === sceneId);
            if (!scene)
                return;
            this.sceneStore.updateScene({
                ...scene,
                backgroundSourceId: image.id,
                backgroundColor: undefined,
            });
            this.publishToOutput();
            this.emitIfChanged();
        });
    }
    notifyAssignSlotSource(sceneId, slotId, sourceId) {
        const scenes = this.sceneStore.getScenes();
        const scene = scenes.find((s) => s.id === sceneId);
        if (!scene) {
            return;
        }
        const updatedScene = {
            ...scene,
            videoSlots: scene.videoSlots.map((s) => s.id === slotId ? { ...s, sourceId } : s),
        };
        this.sceneStore.updateScene(updatedScene);
        this.publishToOutput();
        this.emitIfChanged();
    }
}
